/**
 * ObjectCard - Generic container wrapper for CRM object content.
 *
 * Provides optional border/padding. When `bordered` is false (default),
 * renders children directly in a plain div. Use this to wrap
 * ContactCardContent, CompanyCardContent, etc. in contexts that need
 * a card border (nested inside deal cards, slideouts, pipeline stages).
 */

import { type ReactNode, useCallback } from 'react';

import { cx } from '@/utils/cx';

import { CARD_VARIANT_TINT_CLASSES } from './cardVariantClasses';

export interface ObjectCardProps {
  /** Render with border, padding, and background. Default: false. */
  bordered?: boolean;
  /**
   * Dense padding for compact list rows (e.g. the note slideout's linked
   * records). Swaps the roomy `p-3` for a tight `px-2.5 py-1` so a single-line
   * entity row reads at ~32px instead of ~56px. Default: false (unchanged).
   */
  compact?: boolean;
  /** Visual variant — affects background and border color when bordered. */
  variant?: 'default' | 'destructive' | 'warning';
  /** Makes the card clickable (adds role=button, keyboard handling). */
  onClick?: () => void;
  className?: string;
  children: ReactNode;
}

// Border + background only; padding is applied separately so `compact` can
// tighten it without duplicating each variant.
const VARIANT_BORDERED_CLASSES: Record<string, string> = {
  default: 'rounded-lg border border-border-secondary bg-bg-primary',
  destructive: `rounded-lg ${CARD_VARIANT_TINT_CLASSES.destructive}`,
  warning: `rounded-lg ${CARD_VARIANT_TINT_CLASSES.warning}`,
};

export function ObjectCard({
  bordered = false,
  compact = false,
  variant = 'default',
  onClick,
  className,
  children,
}: ObjectCardProps) {
  const isClickable = !!onClick;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    },
    [onClick]
  );

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      className={cx(
        bordered && VARIANT_BORDERED_CLASSES[variant],
        bordered && (compact ? 'px-2.5 py-1' : 'p-3'),
        isClickable &&
          'cursor-pointer hover:border-border-primary transition-colors',
        className
      )}
    >
      {children}
    </div>
  );
}
